import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import PageContainer from '../../components/layout/PageContainer';
import { useAuth } from '../../hooks/useAuth';
import Overview from './Overview';
import TicketsView from './TicketsView';
import ContractsView from './ContractsView';
import TeamLeadsView from './TeamLeadsView';
import EngineersView from './EngineersView';
import SitesView from './SitesView';
import SpocsView from './SpocsView';
import ReportsView from './ReportsView';
import SettingsView from './SettingsView';
import EmailLogsView from './EmailLogsView';
import CompanyView from './CompanyView';
import EmailTemplatesView from './EmailTemplatesView';
import ManagersView from './ManagersView';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const pages = [
    { id: 'dashboard', name: 'Dashboard', icon: 'dashboard' },
    { id: 'tickets', name: 'Tickets', icon: 'tickets' },
    { id: 'contracts', name: 'AMC Contracts', icon: 'contracts' },
    { id: 'managers', name: 'Managers', icon: 'teamleads' },
    { id: 'engineers', name: 'Engineers', icon: 'engineers' },
    { id: 'teamleads', name: 'Team Leads', icon: 'teamleads' },
    { id: 'spocs', name: 'SPOCs', icon: 'spocs' },
    { id: 'sites', name: 'Sites', icon: 'sites' },
    { id: 'company', name: 'Customer', icon: 'customer' },
    { id: 'reports', name: 'Reports', icon: 'reports' },
    { id: 'email-logs', name: 'Email Logs', icon: 'logs' },
    { id: 'settings', name: 'Settings', icon: 'settings' }
  ];

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    background: 'var(--background)'
  };

  const pageSegment = location.pathname.split('/')[2] || 'dashboard';
  const currentPage = pages.some((page) => page.id === pageSegment) ? pageSegment : 'dashboard';

  const setCurrentPage = (pageId) => {
    navigate(`/admin/${pageId}`);
  };

  const navigateToTickets = (statusFilter = '', priorityFilter = '', activeOnly = false) => {
    if (priorityFilter) {
      navigate(`/admin/tickets?priority=${priorityFilter}${activeOnly ? '&activeOnly=true' : ''}`);
    } else if (statusFilter === 'OVERDUE'){
      navigate(`/admin/tickets?overdue=true`);
    }else if (statusFilter === 'CLOSED') {
      navigate(`/admin/tickets?status=CLOSED&closedToday=true`);
    } else if (statusFilter) {
      navigate(`/admin/tickets?status=${statusFilter}`);
    } else {
      navigate('/admin/tickets');
    }
  };

  const navigateToEngineers = (statusFilter = '') => {
    if (statusFilter) {
      navigate(`/admin/engineers?status=${statusFilter}`);
    } else {
      navigate('/admin/engineers');
    }
  };
  
  const mainContentStyle = {
    flex: 1,
    marginLeft: sidebarOpen ? '260px' : '80px',
    transition: 'margin-left 0.3s ease',
    minHeight: '100vh'
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Overview navigateToTickets={navigateToTickets} navigateToEngineers={navigateToEngineers} setCurrentPage={setCurrentPage} />;
      case 'tickets':
        return <TicketsView />;
      case 'contracts':
        return <ContractsView />;
      case 'teamleads':
        return <TeamLeadsView />;
      case 'managers':
        return <ManagersView />;
      case 'engineers':
        return <EngineersView />;
      case 'sites':
        return <SitesView />;
      case 'spocs':
        return <SpocsView />;
      case 'company':
        return <CompanyView />;
      case 'reports':
        return <ReportsView />;
      case 'email-logs':
        return <EmailLogsView />;
      case 'settings':
        return <SettingsView />;
      default:
        return <Overview />;
    }
  };

  const getPageTitle = () => {
    const page = pages.find(p => p.id === currentPage);
    return page?.name || 'Dashboard';
  };

  const getPageSubtitle = () => {
    const subtitles = {
      dashboard: 'Overview of your system performance',
      tickets: 'Manage and track all service tickets',
      contracts: 'View and manage AMC contracts',
      teamleads: 'Manage teamleads and statistics',
      managers: 'Manage managers and hierarchy',
      engineers: 'Manage field engineers and assignments',
      spocs: 'Manage customer spocs and their assignments',
      sites: 'Manage all customer sites',
      company: 'Manage customer details for site configuration',
      reports: 'Analytics and performance reports',
      'email-logs': 'View system and email debug logs',
      settings: 'System configuration and preferences'
    };
    return subtitles[currentPage] || '';
  };

  return (
    <div style={containerStyle}>
      <Sidebar
        user={user}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pages={pages}
        onLogout={logout}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      <div style={mainContentStyle}>
        <PageContainer
          title={getPageTitle()}
          subtitle={getPageSubtitle()}
        >
          {renderPage()}
        </PageContainer>
      </div>
    </div>
  );
};

export default AdminDashboard;